"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import Icon from "@/components/icons/Icons";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { getBucketAllocation } from "@/utils/financialPageFunctions";

export default function RetirementPlanner() {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const [sipAmount, setSipAmount] = useState(5000);
  const [corpus, setCorpus] = useState(0);

  const investmentPeriod = 20;
  const expectedReturns = 15 / 100;
  const inflation = 3;

  const calculateCorpus = (sip: number) => {
    const r = expectedReturns;
    const n = investmentPeriod * 12;
    const monthlyRate = r / 12;
    const corpusValue = sip * ((Math.pow(1 + monthlyRate, n) - 1) / monthlyRate);
    return Math.round(corpusValue);
  };

  const retirementCards = [
    {
      img: "/next_assets/images/financial-planning/monthly-img.svg",
      title: "Pension",
      percentage: getBucketAllocation(corpus).pension,
      desc: "You can get a pension starting at ₹1,00,000 (growing at 7% every year) for next 35 yrs",
    },
    {
      img: "/next_assets/images/financial-planning/health-img.svg",
      title: "Health Expenses Corpus",
      percentage: getBucketAllocation(corpus).healthEmergency,
      desc: "You can also have a separate Health Corpus of ₹35 Lacs which will also act as an emergency fund",
    },
    {
      img: "/next_assets/images/financial-planning/vacation-img.svg",
      title: "Vacation Budget",
      percentage: getBucketAllocation(corpus).vacation,
      desc: "For next 20 yrs you will be able to take 1 lac worth of local vacation every year + 5 lacs worth of international vacations every alternate year (inflation adjusted)",
    },
    {
      img: "/next_assets/images/financial-planning/legacy-img.svg",
      title: "Legacy Wealth",
      percentage: getBucketAllocation(corpus).legacy,
      desc: "You will also be able to leave a legacy to your children / grand-children after 35 yrs",
    },
  ];

  useEffect(() => {
    setCorpus(calculateCorpus(sipAmount));
  }, [sipAmount]);

  const MAX_SIP = 5000000;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    const numericValue = Number(value);

    if (numericValue <= MAX_SIP) {
      setSipAmount(numericValue);
    } else {
      setSipAmount(MAX_SIP);
    }
  };

  const incrementSIP = () => setSipAmount((prev) => prev + 100);
  const decrementSIP = () => setSipAmount((prev) => Math.max(0, prev - 100));

  return (
    <section className="relative flex flex-col items-center justify-center bg-[radial-gradient(66.97%_50%_at_50%_50%,#4941B7_0%,#1F187D_100%)] px-5 xxs:px-6 py-5 xxs:py-10 text-white mb-17.5 xxs:mb-100 2md:mb-20 2xl:mb-100">
      <h2 className="mb-8 text-center text-2xl xxs:text-xl font-semibold md:text-2xl">
        A GLIMPSE OF YOUR RETIREMENT PLAN
      </h2>

      <div className="w-full max-w-md rounded-6xl border border-[#FFFFFF40] p-5 text-center">
        <p className="mb-3 text-base xxs:text-2xl font-medium">Enter Your SIP Amount</p>

        <div className="flex items-center justify-center gap-4 rounded-4xl border border-[#FFFFFF80] bg-[#FFFFFF1A] px-4 xxs:px-5 py-4 text-xl xxs:text-3xl font-bold text-[#00E281]">
          <button
            className="rounded-full border border-white bg-[#FFFFFF26] p-2 hover:bg-white/20 w-8.5 xxs:w-10.5 h-8.5 xxs:h-10.5 flex items-center justify-center"
            onClick={decrementSIP}
          >
            <Icon.expandableIcon isOpen className="text-white w-4 h-4" />
          </button>

          <div className="relative w-7/12">
            <input
              type="tel"
              value={formatIndianNumber(sipAmount ?? 0, { prefix: "₹" })}
              onChange={handleInputChange}
              className="w-full bg-transparent 2xl:p-2 border-b-2 border-dashed placeholder:text-[#00E281] focus:outline-hidden text-center"
            />
          </div>

          <button
            className="rounded-full border border-white bg-[#FFFFFF26] p-2 hover:bg-white/20 w-8.5 xxs:w-10.5 h-8.5 xxs:h-10.5 flex items-center justify-center"
            onClick={incrementSIP}
          >
            <Icon.expandableIcon className="text-white w-4 h-4" />
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-2 text-sm xxs:text-base text-gray-200">
          {[
            ["Investment Period:", `${investmentPeriod} yrs.`],
            ["Expected Returns:", `${Math.round(expectedReturns * 100)}% CAGR`],
            // ["Inflation Adjustment:", `${inflation}%`],
          ].map(([label, value], i) => (
            <p key={i} className="flex items-center justify-between">
              {label} <span className="font-semibold himanshu">{value}</span>
            </p>
          ))}
        </div>
      </div>

      <div className="relative z-10 mt-8 flex flex-col xxs:flex-row w-full 2md:w-4/5 2xl:w-1/2 items-center justify-between rounded-6xl border border-[#FFFFFF40] bg-[#FFFFFF26] p-4 xxs:p-5 font-medium">
        <span className="text-base xxs:text-2xl font-normal mb-2 xxs:mb-0">Your Retirement Corpus would be</span>
        <span className="text-2xl xxs:text-[32px] font-bold">
          {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(
            corpus,
          )}
        </span>
      </div>

      <div className="hidden 2md:block h-6 xxs:h-10 border-l-4 border-dashed border-white/70" />

      <div className="relative h-6 xxs:h-10 w-full max-w-6xl">
        <div className="absolute hidden 2md:block top-0 left-0 2md:left-[8%] 2md:right-[8%] h-full 2md:h-10 rounded-t-full border-t-4 border-dashed border-white/70" />
        <div className="absolute block 2md:hidden top-27.5 xxs:top-150 2md:top-0 left-[11%] xxs:left-[5.1%] 2md:left-[37%] 2md:h-10 w-8 2md:w-0 border-b-4 2md:border-l-4 border-dashed border-white/70" />
        <div className="absolute top-70 xxs:top-87.5 2md:top-0 left-[11%] xxs:left-[5.1%] 2md:left-[37%] 2md:h-10 w-8 2md:w-0 border-b-4 2md:border-l-4 border-dashed border-white/70" />
        <div className="absolute block 2md:hidden top-113.5 xxs:top-140 2md:top-0 left-[11%] xxs:left-[5.1%] 2md:left-[37%] 2md:h-10 w-8 2md:w-0 border-b-4 2md:border-l-4 border-dashed border-white/70" />
        <div className="absolute top-0 left-8 xxs:left-8 2md:left-[62%] h-160 xxs:h-195 2md:h-10 border-l-4 border-dashed border-white/70" />
        <div className="absolute block 2md:hidden top-160 xxs:top-195 2md:top-0 left-[11%] xxs:left-[5.1%] 2md:left-[37%] 2md:h-10 w-8 2md:w-0 border-b-4 2md:border-l-4 border-dashed border-white/70" />
      </div>

      <div className=" relative w-full max-w-6xl flex-col items-end 2md:items-stretch 2md:flex-row gap-6 flex 2md:px-0">
        {retirementCards.map(({ img, title, percentage, desc }, index) => {
          const cardValue = corpus * (percentage / 100);
          return (
            <div
              key={index}
              className="w-4/5 xxs:w-[90%] 2md:w-full rounded-3xl xxs:rounded-6xl border border-[#FFFFFF40] bg-[#FFFFFF26] p-3 xxs:p-5 2xl:p-6 text-left"
            >
              <div className="mb-2 xxs:mb-5 2xl:mb-4 flex items-center gap-3">
                <Image
                  src={img}
                  width={55}
                  height={55}
                  alt={title}
                  className="w-10 xxs:w-13.75 h-10 xxs:h-13.75"
                />
                <div className="text-base xxs:text-lg font-semibold">{title}</div>
              </div>

              <p className="text-xl xxs:text-[26px] font-bold">
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                  maximumFractionDigits: 0,
                }).format(cardValue)}
              </p>

              <p className="mt-2 text-sm font-normal text-[#FFFFFFCC] hidden 2md:block">{desc}</p>

              <div className="mt-2 2md:hidden">
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedCard === index ? "max-h-40 opacity-100" : "max-h-6 opacity-100"}`}
                >
                  <p
                    className={`text-sm xxs:text-base font-normal text-[#FFFFFFCC] ${expandedCard === index ? "" : "line-clamp-1"}`}
                  >
                    {desc}
                  </p>
                </div>

                {expandedCard === index ? (
                  <button
                    className="mt-1 text-xs xxs:text-base font-semibold text-white"
                    onClick={() => setExpandedCard(null)}
                  >
                    Read less
                  </button>
                ) : (
                  <button
                    className="text-xs xxs:text-base font-semibold text-white"
                    onClick={() => setExpandedCard(index)}
                  >
                    Read more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex flex-col gap-4 xs:flex-row">
        <Link
          href="/financial-planning/retirement-planning/pre-retirement"
          className="block rounded-1.5xl xxs:rounded-2xl border-[3px] border-[#211A8080] bg-white px-10 py-2 xxs:py-3 2xl:py-5 text-base xxs:text-xl font-semibold text-[#211A80]"
        >
          Start my Retirement now
        </Link>
        <Link
          href="tel:+91-9660032889"
          className="shine-button-hover rounded-1.5xl xxs:xxs:rounded-2xl border-[3px] border-white bg-transparent px-10 py-2 xxs:py-3 2xl:py-5 text-base xxs:text-xl font-semibold text-white"
        >
          Connect with our Expert
        </Link>
      </div>

      <p className="mt-6 max-w-2xl text-center text-[10px] xxs:text-xs font-light text-white/60">
        <b className="text-white/80">Disclaimer:</b> These are illustrative estimates and do not guarantee future
        returns. Mutual fund investments are subject to market risks, please read all scheme related documents carefully
        before investing.
      </p>
    </section>
  );
}
